import { useEffect, useState } from 'react';
import { AlertTriangle, Eye } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { ExceptionFilters } from '@/components/features/exceptions/ExceptionFilters';
import { ExceptionLogDetail } from '@/components/features/exceptions/ExceptionLogDetail';
import { ExceptionIncidentDetail } from '@/components/features/exceptions/ExceptionIncidentDetail';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useI18n } from '@/contexts/I18nContext';
import type { ExceptionLog, ExceptionIncident, ExceptionStats, ExceptionFilters as ExceptionFiltersType } from '@/types/exception.types';

function severityBadgeClass(severity: string) {
  switch (severity) {
    case 'info': return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
    case 'warn': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
    case 'error': return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    case 'critical': return 'bg-red-100 text-red-900 border-red-400 dark:bg-red-900 dark:text-red-100 dark:border-red-700 font-bold';
    default: return '';
  }
}

function sourceBadgeClass(source: string) {
  switch (source) {
    case 'http': return 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600';
    case 'ws': return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800';
    case 'cron': return 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800';
    case 'event': return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
    case 'process': return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    default: return '';
  }
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
}

const initialFilters: ExceptionFiltersType = { page: 1, limit: 50 };

export default function ExceptionLogs() {
  const { tp } = useI18n();
  const { get, loading } = useApi();
  const logsPage = usePagination();
  const incidentsPage = usePagination();

  const [activeTab, setActiveTab] = useState<'logs' | 'incidents'>('logs');
  const [filters, setFilters] = useState<ExceptionFiltersType>(initialFilters);
  const [logs, setLogs] = useState<ExceptionLog[]>([]);
  const [incidents, setIncidents] = useState<ExceptionIncident[]>([]);
  const [stats, setStats] = useState<ExceptionStats | null>(null);
  const [selectedLog, setSelectedLog] = useState<ExceptionLog | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<ExceptionIncident | null>(null);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else {
      fetchIncidents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  const fetchStats = async () => {
    try {
      const response: any = await get('/api/v1/admin/exception-logs/stats');
      if (response) setStats(response);
    } catch {
      // silently ignore
    }
  };

  const fetchLogs = async () => {
    try {
      const params: Record<string, any> = { page: filters.page, limit: filters.limit };
      if (filters.error_code) params.error_code = filters.error_code;
      if (filters.module) params.module = filters.module;
      if (filters.severity) params.severity = filters.severity;
      if (filters.source) params.source = filters.source;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;

      const response: any = await get('/api/v1/admin/exception-logs', params);
      if (response) {
        const list = Array.isArray(response) ? response : response.list ?? response.data ?? [];
        setLogs(list);
        const meta = response.meta ?? response;
        logsPage.updatePagination({
          total_pages: meta.total_pages ?? meta.totalPages ?? 0,
          total: meta.total ?? 0,
        });
      }
    } catch {
      // silently ignore
    }
  };

  const fetchIncidents = async () => {
    try {
      const params: Record<string, any> = { page: filters.page, limit: filters.limit };
      if (filters.module) params.module = filters.module;
      if (filters.error_code) params.error_code = filters.error_code;

      const response: any = await get('/api/v1/admin/exception-incidents', params);
      if (response) {
        const list = Array.isArray(response) ? response : response.list ?? response.data ?? [];
        setIncidents(list);
        const meta = response.meta ?? response;
        incidentsPage.updatePagination({
          total_pages: meta.total_pages ?? meta.totalPages ?? 0,
          total: meta.total ?? 0,
        });
      }
    } catch {
      // silently ignore
    }
  };

  const handleFiltersChange = (newFilters: ExceptionFiltersType) => {
    setFilters(newFilters);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as 'logs' | 'incidents');
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const logsColumns = [
    {
      header: tp.exceptions.columns.time,
      accessor: (row: ExceptionLog) => (
        <span className="text-xs font-mono whitespace-nowrap">{formatTime(row.occurred_at)}</span>
      ),
    },
    {
      header: tp.exceptions.columns.code,
      accessor: (row: ExceptionLog) => (
        <Badge variant="outline" className={`text-xs ${severityBadgeClass(row.severity)}`}>
          {row.error_code}
        </Badge>
      ),
    },
    {
      header: tp.exceptions.columns.severity,
      accessor: (row: ExceptionLog) => (
        <Badge variant="outline" className={`text-xs ${severityBadgeClass(row.severity)}`}>
          {row.severity}
        </Badge>
      ),
    },
    {
      header: tp.exceptions.columns.module,
      accessor: 'module' as keyof ExceptionLog,
      className: 'text-sm',
    },
    {
      header: tp.exceptions.columns.message,
      accessor: (row: ExceptionLog) => (
        <span className="text-xs text-muted-foreground">
          {row.message.length > 80 ? row.message.slice(0, 80) + '…' : row.message}
        </span>
      ),
    },
    {
      header: tp.exceptions.columns.origin,
      accessor: (row: ExceptionLog) =>
        row.origin_file ? (
          <code className="text-xs font-mono text-muted-foreground">
            {row.origin_file.length > 30 ? '…' + row.origin_file.slice(-30) : row.origin_file}
            {row.origin_line != null ? `:${row.origin_line}` : ''}
          </code>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      header: tp.exceptions.columns.source,
      accessor: (row: ExceptionLog) => (
        <Badge variant="outline" className={`text-xs ${sourceBadgeClass(row.source)}`}>
          {row.source}
        </Badge>
      ),
    },
  ];

  const incidentColumns = [
    {
      header: tp.exceptions.columns.lastSeen,
      accessor: (row: ExceptionIncident) => (
        <span className="text-xs font-mono whitespace-nowrap">{formatTime(row.last_seen_at)}</span>
      ),
    },
    {
      header: tp.exceptions.columns.code,
      accessor: (row: ExceptionIncident) => (
        <Badge variant="outline" className="text-xs">{row.error_code}</Badge>
      ),
    },
    {
      header: tp.exceptions.columns.module,
      accessor: 'module' as keyof ExceptionIncident,
      className: 'text-sm',
    },
    {
      header: tp.exceptions.columns.occurrences,
      accessor: (row: ExceptionIncident) => (
        <span className="font-bold text-sm">{row.occurrence_count}</span>
      ),
    },
    {
      header: tp.exceptions.columns.firstSeen,
      accessor: (row: ExceptionIncident) => (
        <span className="text-xs text-muted-foreground">{formatTime(row.first_seen_at)}</span>
      ),
    },
    {
      header: tp.exceptions.columns.status,
      accessor: (row: ExceptionIncident) =>
        row.resolved ? (
          <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {tp.exceptions.columns.resolved}
          </Badge>
        ) : (
          <Badge variant="destructive" className="text-xs">{tp.exceptions.columns.open}</Badge>
        ),
    },
  ];

  const renderLogActions = (row: ExceptionLog) => (
    <Button
      variant="ghost" size="icon" className="h-8 w-8"
      onClick={(e) => { e.stopPropagation(); setSelectedLog(row); }}
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const renderIncidentActions = (row: ExceptionIncident) => (
    <Button
      variant="ghost" size="icon" className="h-8 w-8"
      onClick={(e) => { e.stopPropagation(); setSelectedIncident(row); }}
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const currentPagination = activeTab === 'logs' ? logsPage : incidentsPage;

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-orange-500" />
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{tp.exceptions.title}</h1>
            <p className="text-sm text-muted-foreground">{tp.exceptions.subtitle}</p>
          </div>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{tp.exceptions.stats.total}</p>
            <p className="text-2xl font-bold mt-1">{stats.total.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{tp.exceptions.stats.last24h}</p>
            <p className="text-2xl font-bold mt-1">{stats.last_24h.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{tp.exceptions.stats.topModule}</p>
            {stats.byModule[0] ? (
              <>
                <p className="text-sm font-bold mt-1 truncate">{stats.byModule[0].module}</p>
                <p className="text-xs text-muted-foreground">{stats.byModule[0].count}x</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">—</p>
            )}
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{tp.exceptions.stats.topCode}</p>
            {stats.byCode[0] ? (
              <>
                <p className="text-sm font-bold mt-1">{stats.byCode[0].code}</p>
                <p className="text-xs text-muted-foreground">{stats.byCode[0].count}x</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">—</p>
            )}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="logs">{tp.exceptions.tabs.logs}</TabsTrigger>
          <TabsTrigger value="incidents">{tp.exceptions.tabs.incidents}</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <div className="flex gap-4 h-[calc(100vh-380px)]">
            <div className="hidden lg:block h-full overflow-y-auto">
              <ExceptionFilters filters={filters} onChange={handleFiltersChange} />
            </div>
            <div className="flex-1 min-w-0">
              {loading ? (
                <LoadingSkeleton type="table" rows={10} />
              ) : logs.length === 0 ? (
                <EmptyState
                  icon={<AlertTriangle className="h-16 w-16" />}
                  title={tp.exceptions.emptyLogs}
                  description={tp.exceptions.emptyLogsDesc}
                />
              ) : (
                <DataTable
                  data={logs}
                  columns={logsColumns}
                  actions={renderLogActions}
                  pagination={{
                    currentPage: logsPage.page,
                    totalPages: logsPage.totalPages,
                    total: logsPage.total,
                    onPageChange: (p) => setFilters((prev) => ({ ...prev, page: p })),
                  }}
                  emptyMessage={tp.exceptions.emptyLogs}
                />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="incidents">
          <div className="flex gap-4 h-[calc(100vh-380px)]">
            <div className="hidden lg:block h-full overflow-y-auto">
              <ExceptionFilters filters={filters} onChange={handleFiltersChange} />
            </div>
            <div className="flex-1 min-w-0">
              {loading ? (
                <LoadingSkeleton type="table" rows={10} />
              ) : incidents.length === 0 ? (
                <EmptyState
                  icon={<AlertTriangle className="h-16 w-16" />}
                  title={tp.exceptions.emptyIncidents}
                  description={tp.exceptions.emptyIncidentsDesc}
                />
              ) : (
                <DataTable
                  data={incidents}
                  columns={incidentColumns}
                  actions={renderIncidentActions}
                  pagination={{
                    currentPage: incidentsPage.page,
                    totalPages: incidentsPage.totalPages,
                    total: incidentsPage.total,
                    onPageChange: (p) => setFilters((prev) => ({ ...prev, page: p })),
                  }}
                  emptyMessage={tp.exceptions.emptyIncidents}
                />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ExceptionLogDetail
        log={selectedLog}
        open={selectedLog !== null}
        onClose={() => setSelectedLog(null)}
      />

      <ExceptionIncidentDetail
        incident={selectedIncident}
        open={selectedIncident !== null}
        onClose={() => setSelectedIncident(null)}
        onResolved={() => { fetchIncidents(); fetchStats(); }}
      />
    </PageContainer>
  );
}
